import React, { useEffect, useMemo } from "react";
import { getFilterJson } from "../../../../../AppView/AppViewUtils";
import { getSeverityFilter } from "../../../../../Findings/FindingsFilters/FilterUtils";
import "./FindingsSummaryKPI.scss";
import useSummary from "../../../../useSummary";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../../../General/useSeverityAsPriority";
import {
  Loader,
  SkeletonLoader,
  TextButton,
  useIcon,
  normalizeNumbers,
  formatNumber,
} from "@SeemplicityDev/shared";
import WidgetError from "../../../WidgetError/WidgetError";
import useFindingCounts from "../../../../../Findings/FindingsData/useFindingCounts";
import useRetry from "../../../../useRetry";
import useWidgetFilterNavigator, {
  type FilterCondition,
} from "../../../../useWidgetFilterNavigator";
import type { SelectedScope } from "../../../../../types/scope";

// Parsed `summaryData` payload from useSummary is a list of row tuples:
// [severityValue, count]. Consumers narrow the `unknown` boundary themselves.
type SummaryRow = [number, number];

interface FindingsSummaryKPIProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
  isForExport?: boolean;
}

const FindingsSummaryKPI: React.FC<FindingsSummaryKPIProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  setIsReadyToExport,
  isForExport,
}) => {
  const { getSeverityField } = useSeverityAsPriority();
  const { getSvg } = useIcon(isForExport ? "LIGHT" : undefined);

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id,
  });

  const {
    summaryData: allFindingsCountRaw,
    summaryDataError: allFindingsCountError,
    summaryLoading: allFindingsCountLoading,
    refetchSummary: refetchAllFindingsCount,
  } = useSummary({
    variables: {
      name: "FINDINGS_VIEW",
      selection: ["finding.id"],
      group_by: [],
      aggregate_functions: ["COUNT"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute || null,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  // Runtime-preserved: this ungrouped COUNT summary is consumed directly as a
  // number (arithmetic, equality and formatNumber/normalizeNumbers below).
  const allFindingsCount = allFindingsCountRaw as number | null;

  const {
    totalRawCount,
    loading: totalRawCountLoading,
    error: totalRawCountRequestError,
    refetch: refetchTotalRawCount,
  } = useFindingCounts(
    {
      filterToExecute: filterToExecute || null,
      scopeId: selectedScope?.value?.id,
      scopeToExecute: scopeToExecute || null,
    },
    (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
    { notifyOnNetworkStatusChange: true }
  );

  const {
    finalError: totalRawCountError,
    resetRetries: resetTotalRawCountRetries,
  } = useRetry({
    requestError: totalRawCountRequestError,
    refetch: refetchTotalRawCount,
  });

  useEffect(() => {
    if (totalRawCount !== undefined && !totalRawCountLoading) {
      resetTotalRawCountRetries();
    }
  }, [totalRawCount, totalRawCountLoading]);

  const {
    summaryData: bySeverityDataRaw,
    summaryDataError: bySeverityDataError,
    summaryLoading: bySeverityLoading,
    refetchSummary: refetchBySeverity,
  } = useSummary({
    variables: {
      name: "FINDINGS_VIEW",
      selection: ["finding.id"],
      group_by: ["finding.severity"],
      aggregate_functions: ["COUNT"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute || null,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const bySeverityData = bySeverityDataRaw as SummaryRow[] | null;

  const currentHighestSeverity = useMemo(() => {
    if (!!bySeverityData?.length) {
      const mostSevere = bySeverityData?.reduce<SummaryRow>(
        (highest, current) => {
          if (current[0] > highest[0] && current[1] > 0) {
            return current;
          }

          return highest;
        },
        [-Infinity, 0]
      );
      return {
        severity: mostSevere[0],
        count: mostSevere[1],
      };
    } else {
      return null;
    }
  }, [bySeverityData]);

  const {
    totalRawCount: highestTotalRawCount,
    loading: highestTotalRawCountLoading,
    error: highestTotalRawCountRequestError,
    refetch: refetchHighestTotalRawCount,
  } = useFindingCounts(
    {
      filterToExecute: filterToExecute,
      scopeId: selectedScope?.value?.id,
      scopeToExecute: scopeToExecute,
      // getFilterJson returns a JSON string, which is the runtime value main
      // forwards to the query. The hook types this field as
      // `string[] | Record<string, unknown>`, so cast to preserve behavior.
      filterFieldsToOverride: getFilterJson(
        getSeverityFilter(currentHighestSeverity?.severity?.toString())
      ) as unknown as Record<string, unknown>,
    },
    (!scopeToExecute && !selectedScope?.value?.id) ||
      !filterToExecute ||
      !currentHighestSeverity?.severity,
    { notifyOnNetworkStatusChange: true }
  );

  const {
    finalError: highestTotalRawCountError,
    resetRetries: resetHighestTotalRawCountRetries,
  } = useRetry({
    requestError: highestTotalRawCountRequestError,
    refetch: refetchHighestTotalRawCount,
  });

  useEffect(() => {
    if (highestTotalRawCount !== undefined && !highestTotalRawCountLoading) {
      resetHighestTotalRawCountRetries();
    }
  }, [highestTotalRawCount, highestTotalRawCountLoading]);

  const allFindingsDeductionPercentage = useMemo(() => {
    if (!allFindingsCount || !totalRawCount) {
      return 0;
    }
    return Math.round(100 - (allFindingsCount / totalRawCount) * 100);
  }, [allFindingsCount, totalRawCount]);

  const criticalFindingsDeductionPercentage = useMemo(() => {
    if (!currentHighestSeverity?.count || !highestTotalRawCount) {
      return 0;
    }

    return Math.round(
      100 - (currentHighestSeverity?.count / highestTotalRawCount) * 100
    );
  }, [currentHighestSeverity?.count, highestTotalRawCount]);

  const displayDeductions = useMemo(
    () => allFindingsDeductionPercentage > 10,
    [allFindingsDeductionPercentage]
  );

  const displayHighestSeverity = useMemo(
    () => displayDeductions && criticalFindingsDeductionPercentage > 5,
    [criticalFindingsDeductionPercentage]
  );

  const displayedRawFindingsCount = useMemo(() => {
    // Non-null assertions preserve main's JS runtime: when `totalRawCount` is
    // undefined, `undefined < 1e9` is false, so it falls through to
    // normalizeNumbers (which coerces the falsy value to 0). The `!` is erased
    // at runtime.
    if (totalRawCount! < 1000000000) {
      return formatNumber(totalRawCount);
    }

    return normalizeNumbers(totalRawCount!);
  }, [totalRawCount]);

  const displayedAllFindingsCount = useMemo(() => {
    if (allFindingsCount! < 1000000000) {
      return formatNumber(allFindingsCount);
    }

    return normalizeNumbers(allFindingsCount!);
  }, [allFindingsCount]);

  const noData = useMemo(() => {
    return (
      (!allFindingsCountLoading && allFindingsCount === 0) ||
      (!totalRawCountLoading && totalRawCount === 0)
    );
  }, [
    allFindingsCountLoading,
    allFindingsCount,
    totalRawCountLoading,
    totalRawCount,
  ]);

  const handleSeverityClick = () => {
    onFilterClick([
      [
        "in",
        "remediation_item.severity",
        currentHighestSeverity?.severity?.toString(),
      ],
    ] as unknown as FilterCondition[]);
  };

  useEffect(() => {
    setIsReadyToExport?.(
      !!displayedAllFindingsCount && !!displayedRawFindingsCount
    );
  }, [
    displayedAllFindingsCount,
    displayedRawFindingsCount,
    setIsReadyToExport,
  ]);

  useEffect(() => {
    if (!!allFindingsCountError || !!totalRawCountError) {
      setIsReadyToExport?.(true);
    }
  }, [allFindingsCountError, totalRawCountError]);

  useEffect(() => {
    setIsReadyToExport?.(!allFindingsCountLoading || !totalRawCountLoading);
  }, [allFindingsCountLoading, totalRawCountLoading]);

  return (
    <div className={"findings-summary-kpi"}>
      {allFindingsCountError || totalRawCountError ? (
        <WidgetError
          onWidgetsReload={() => {
            refetchAllFindingsCount();
            refetchTotalRawCount();
          }}
        />
      ) : noData ? (
        <Loader
          data-testid={"findings-summary-no-data"}
          className={"empty-state-no-match-to-filter"}
          center
          noBackground
          icon={"seem-icon-manualfinding"}
          subtitle={"There are no Findings for this Scope and Filter"}
        />
      ) : (
        <>
          <div className="findings-summary-kpi-top">
            <div className="findings-counts-section">
              {!totalRawCountLoading && !!totalRawCount ? (
                <div className="raw-findings-count">
                  <span className="findings-number">
                    {displayedRawFindingsCount}
                  </span>
                  <span className="findings-text">Raw Findings</span>
                </div>
              ) : (
                <div className={"total-findings-loader-count-wrap"}>
                  <SkeletonLoader width={"60px"} height={"20px"} />
                  <SkeletonLoader width={"84px"} height={"14px"} />
                </div>
              )}
              <i className="seem-icon-right-arrow" />
              <img
                src={getSvg("seem-magic")}
                alt="seemplicity magic"
                className="magic-seem-img"
              />
              <i className="seem-icon-right-arrow" />
              {!allFindingsCountLoading && !!allFindingsCount ? (
                <div className="total-findings-count">
                  <span className="findings-number">
                    {displayedAllFindingsCount}
                  </span>
                  <span className="findings-text">Findings</span>
                </div>
              ) : (
                <div className={"total-findings-loader-count-wrap"}>
                  <SkeletonLoader width={"60px"} height={"20px"} />
                  <SkeletonLoader width={"84px"} height={"14px"} />
                </div>
              )}
            </div>
          </div>
          <div className="findings-summary-kpi-bottom">
            {displayDeductions && !!allFindingsDeductionPercentage && (
              <div className="all-findings-deduction-percentage">
                <span className="all-findings-deduction-percentage-text">
                  All Findings
                </span>
                <div className={"deduction-percentage-chip"}>
                  <i className="seem-icon-arrow-down down" />
                  <span className="all-findings-deduction-percentage-number">
                    {allFindingsDeductionPercentage}%
                  </span>
                </div>
              </div>
            )}
            {displayHighestSeverity &&
              (highestTotalRawCount !== 0 &&
              currentHighestSeverity?.count !== 0 ? (
                <div
                  className="critical-findings-chip"
                  onClick={handleSeverityClick}
                >
                  <div className="severity-square">
                    <div
                      className="square"
                      style={{
                        backgroundColor: getSeverityField(
                          currentHighestSeverity?.severity,
                          SeverityFieldsEnum.COLOR
                        ),
                      }}
                    />
                    <span className="severity-name">
                      {`${getSeverityField(
                        currentHighestSeverity?.severity,
                        SeverityFieldsEnum.NAME
                      )} Findings (${formatNumber(currentHighestSeverity?.count)}`}
                      )
                    </span>
                  </div>
                  <div className="critical-count-deduction">
                    <i className="seem-icon-arrow-down down" />
                    <span>{criticalFindingsDeductionPercentage}%</span>
                  </div>
                </div>
              ) : highestTotalRawCountLoading || bySeverityLoading ? (
                <SkeletonLoader width={"160px"} height={"42px"} />
              ) : (
                !!highestTotalRawCountError ||
                (!!bySeverityDataError && (
                  <div>
                    Error loading,{" "}
                    <TextButton
                      data-testid={"kpi-try-loading-again"}
                      buttonStyle={"textLink"}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        refetchBySeverity();
                        refetchHighestTotalRawCount();
                      }}
                      label={"Try again"}
                    />{" "}
                  </div>
                ))
              ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FindingsSummaryKPI;
